/**
 * The state machine + URL-param parsing shared by the two "enter your email and
 * we'll send you something" screens: `/forgot` and `/signup/resend_activation`.
 *
 * Those pages are the same page on the live site — same layout, same single
 * field, same three post-submit outcomes keyed off the HTTP status — and differ
 * only in their heading, their body copy, their button label and which endpoint
 * they post to. That difference is a `RequestCopy` object; everything else lives
 * here so the two screens can never drift apart behaviourally.
 *
 * Deliberately NO `"use client"`: the pages are server components and read
 * `searchParams` there. A plain function exported from a client module is a
 * client *reference*, not a callable — calling one during SSR fails with "a is
 * not a function" — so the pure helpers live here and the hook lives in
 * `auth-request-model`.
 */

/* --- Per-page copy --------------------------------------------------------- */

/** Everything that differs between `/forgot` and `/signup/resend_activation`. */
export interface RequestCopy {
  /** The `<h1>`. */
  title: string;
  /** The body paragraph above the form. */
  message: string;
  /** The single field's placeholder + accessible name. */
  placeholder: string;
  /** The submit button's label. */
  submit: string;
  /** Shown on a 2xx; the form is replaced by the back-link. */
  success: string;
  /** Shown on a 404; the form stays so the address can be corrected. */
  notFound: string;
  /** Shown on any other failure. */
  failed: string;
}

/**
 * Live swaps the whole form for this link once the request succeeds. Identical
 * on both pages, so it is not part of `RequestCopy`.
 */
export const BACK_TO_LOGIN = "Back to Login Page";

/* --- URL params ------------------------------------------------------------ */

/**
 * `?scenario=` → a forced display state, so every state is reviewable without a
 * backend round-trip. Mapped onto these pages' four states:
 *   - `empty`      the live page: heading, message, empty form (the default)
 *   - `loading`    mid-send, the button busy
 *   - `populated`  the send succeeded — success notice + "Back to Login Page"
 *   - `error`      the address is unknown to the backend (the live 404 copy)
 */
export type RequestScenario = "populated" | "empty" | "loading" | "error";

export function parseRequestScenario(
  raw: string | string[] | undefined,
): RequestScenario {
  const value = Array.isArray(raw) ? raw[0] : raw;
  switch (value) {
    case "populated":
    case "loading":
    case "error":
      return value;
    default:
      return "empty";
  }
}

/* --- State machine --------------------------------------------------------- */

/** What the page is showing. `sent` is the only state that hides the form. */
export type RequestState = "idle" | "sending" | "sent" | "notFound" | "failed";

/** The scenario override, expressed as the state it forces. */
export function stateForScenario(scenario: RequestScenario): RequestState {
  switch (scenario) {
    case "loading":
      return "sending";
    case "populated":
      return "sent";
    case "error":
      return "notFound";
    default:
      return "idle";
  }
}

/** The notice copy for a state, or null when there is nothing to announce. */
export function noticeForState(
  copy: RequestCopy,
  state: RequestState,
): string | null {
  switch (state) {
    case "sent":
      return copy.success;
    case "notFound":
      return copy.notFound;
    case "failed":
      return copy.failed;
    default:
      return null;
  }
}
